import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import NotificationBar from "./home/NotifictionBar";
import MainRegistrationForm from "../_auth/registration/MainRegistrationForm";
import { motion } from "framer-motion";
import api from "../lib/api.ts";
import { useLanguage } from "../context/LanguageContext";
import { getPageSettings } from "../services/pagesettingServices.ts";
import usePageMeta from "../hooks/usePageMeta.js";

export default function Seller() {
  usePageMeta("seller");
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const [openIndex, setOpenIndex] = useState(null);
  const [sellerPackages, setSellerPackages] = useState([]);
  const [packageFilter, setPackageFilter] = useState("All"); // Add this state
  const [pageSettings, setPageSettings] = useState(null);
  const [sellerFaqs, setSellerFaqs] = useState([]);

  const slideFromLeft = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const slideFromRight = {
    hidden: { opacity: 0, x: 100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/sellerpackages`);
        setSellerPackages(response.data); // <-- set API data here
      } catch (err) {
        console.error("❌ Error fetching seller data:", err);
      }
    };

    fetchData();
  }, []);

  // Fetch page settings API
  useEffect(() => {
    const fetchPageSettings = async () => {
      try {
        const response = await api.get("/pagesettings/clientsellerpage", {
          params: { lang: language },
        });

        if (response.data) {
          setPageSettings(response.data);
        }
      } catch (error) {
        console.error("❌ Error fetching page settings:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }
    };

    const fetchSellerFaqs = async () => {
      try {
        const response = await getPageSettings({
          page: "sellerfaqs",
          lang: language,
        });
        setSellerFaqs(response.seller_faqs || []);
      } catch (error) {
        console.error("❌ Error fetching seller FAQs:", error);
      }
    };

    fetchPageSettings();
    fetchSellerFaqs();
  }, [language]);

  // Get meta fields from pageSettings
  const metaDescription =
    pageSettings?.meta_fields?.[0]?.meta_description?.value || "";
  const metaKeywords =
    pageSettings?.meta_fields?.[0]?.meta_keywords?.value || "";
  const pageTitle = pageSettings?.hero_banner?.[0]?.title?.value;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      </Helmet>
      <NotificationBar />
      <style>{`
        .blog-content .ql-ui { display: none; }
        .blog-content ol { list-style: decimal; margin-left: 1.25rem; }
        .blog-content ul { list-style: disc; margin-left: 1.25rem; }
        .blog-content li { margin: 0.375rem 0; }
        .blog-content li[data-list="bullet"] { list-style-type: disc; }
        .blog-content li[data-list="ordered"] { list-style-type: decimal; }
        .blog-content h1, .blog-content h2, .blog-content h3 { color: #111827; }
        .blog-content p { color: #374151; white-space: pre-wrap; }
        .blog-content a { color: #2563eb; text-decoration: underline; }
      `}</style>
      <section
        className="max-w-7xl justify-center mx-auto items-center"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Top Banner */}
        <div
          className="relative w-full h-[400px] flex flex-col items-center justify-center text-center bg-darkGraybg px-4"
          style={{
            backgroundImage: pageSettings?.hero_banner?.[0]?.imageUrl?.value
              ? `url('${pageSettings.hero_banner[0].imageUrl.value}')`
              : "url('https://st2.depositphotos.com/3489481/11303/i/950/depositphotos_113034082-stock-photo-man-driver-happy-smiling-showing.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>

          <div className="relative z-10">
            <motion.h1
              className="mt-[40px] md:mt-[20px] text-white text-[40px] md:text-4xl font-bold mb-3"
              initial={{ y: -100, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ type: "spring", stiffness: 100, damping: 10 }}
            >
              {pageSettings?.hero_banner?.[0]?.title?.value || ""}
            </motion.h1>
            <br />
            <div className="relative z-10 max-w-3xl mx-auto">
              <motion.p
                className="text-[#F5F5F5] mb-5 text-[24px]"
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                {pageSettings?.hero_banner?.[0]?.subtitle?.value || ""}
              </motion.p>
              <br />

              {/* Button with border sweep */}
              <a
                href={pageSettings?.hero_banner?.[0]?.btnLink?.value || "#"}
                className="inline-block"
              >
                <button className="relative px-6 py-2 font-medium text-[#ffffff] bg-[#F15A29] rounded overflow-hidden group mt-4">
                  <span className="absolute top-0 left-0 w-0 h-[2px] bg-[#ffffff] transition-all duration-500 group-hover:w-full"></span>
                  <span className="absolute bottom-0 right-0 w-0 h-[2px] bg-[white] transition-all duration-500 group-hover:w-full"></span>
                  <span className="absolute top-0 right-0 h-0 w-[2px] bg-[white] transition-all duration-500 group-hover:h-full"></span>
                  <span className="absolute bottom-0 left-0 h-0 w-[2px] bg-[white] transition-all duration-500 group-hover:h-full"></span>
                  {pageSettings?.hero_banner?.[0]?.btnText?.value ||
                    translate("sellerPage.becomeSeller")}
                </button>
              </a>
            </div>
          </div>
        </div>

        {/* Register Section */}

        <div className="bg-gray-50 py-12 px-4">
          <div
            className={`max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center bg-white shadow-md rounded-lg p-6 md:p-10 ${
              isRTL ? "md:grid-flow-col-dense" : ""
            }`}
            dir={isRTL ? "rtl" : "ltr"}
            lang={language}
          >
            {/* Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }} // triggers every time
              variants={isRTL ? slideFromRight : slideFromLeft}
              className={isRTL ? "md:order-2" : "md:order-1"}
            >
              <h2 className="text-[#F15A29] font-bold text-[32px] mb-2">
                {pageSettings?.right_image_section?.[0]?.title?.value || ""}
              </h2>
              <h3 className="text-gray-900 font-bold text-[32px] mb-4">
                {pageSettings?.right_image_section?.[0]?.subtitle?.value ||
                  translate("sellerPage.withKhatAlJazeera")}
              </h3>
              <div
                className="blog-content prose prose-lg text-gray-600 mb-6"
                dangerouslySetInnerHTML={{
                  __html:
                    pageSettings?.right_image_section?.[0]?.description
                      ?.value || "",
                }}
              />
              <a
                href={
                  pageSettings?.right_image_section?.[0]?.btnLink?.value ||
                  "/register"
                }
                className="inline-block"
              >
                {/* Button with border sweep */}
                <button className="relative px-6 py-2 font-medium text-white bg-gray-900 rounded overflow-hidden group mt-2">
                  <span className="absolute top-0 left-0 w-0 h-[2px] bg-[#F15A29] transition-all duration-500 group-hover:w-full"></span>
                  <span className="absolute bottom-0 right-0 w-0 h-[2px] bg-[#F15A29] transition-all duration-500 group-hover:w-full"></span>
                  <span className="absolute top-0 right-0 h-0 w-[2px] bg-[#F15A29] transition-all duration-500 group-hover:h-full"></span>
                  <span className="absolute bottom-0 left-0 h-0 w-[2px] bg-[#F15A29] transition-all duration-500 group-hover:h-full"></span>
                  {pageSettings?.right_image_section?.[0]?.btnText?.value ||
                    translate("sellerPage.registerNow")}
                </button>
              </a>
            </motion.div>

            {/* Image */}
            <motion.div
              className={`flex justify-center ${
                isRTL ? "md:order-1" : "md:order-2"
              }`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }} // triggers every time
              variants={isRTL ? slideFromLeft : slideFromRight}
            >
              <div className="bg-white p-1 shadow-md rounded-lg border border-gray-200">
                <img
                  src={
                    pageSettings?.right_image_section?.[0]?.imageUrl?.value ||
                    "https://media.istockphoto.com/id/483978526/photo/close-up-of-handshake-in-auto-show-or-salon.jpg?s=612x612&w=0&k=20&c=p4a9HRrrQgY52Bzpky3b-YNuHyumzlst_p57uhKpFRo="
                  }
                  alt={
                    pageSettings?.right_image_section?.[0]?.altText?.value ||
                    "Car Key Exchange"
                  }
                  className="rounded-lg w-full "
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        className="bg-[#FAFAFA] py-12 px-4 max-w-7xl justify-center mx-auto items-center"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <motion.div
          className="m text-center mb-10"
          initial={{ x: -100, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: false }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-[32px] font-bold">
            {translate("sellerPage.choosePlan")
              .split(" ")
              .map((word, index) => (
                <span
                  key={index}
                  className={
                    word.toLowerCase() === "choose" ||
                    word.toLowerCase() === "اختر"
                      ? "text-[#F15A29]"
                      : ""
                  }
                >
                  {word}{" "}
                </span>
              ))}
          </h2>
          <p className="text-gray-600 font-600 text-[16px] mt-2">
            {translate("sellerPage.choosePlanSubtitle")}
          </p>
        </motion.div>

        {/* Filter Buttons */}

        <motion.div
          initial={{ x: -100, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: false }}
          transition={{ duration: 0.8 }}
        >
          <div
            className={`flex justify-center gap-4 mt-4 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <button
              className={`px-4 py-2 rounded-full font-semibold border ${
                packageFilter === "All"
                  ? "bg-gray-500 text-white"
                  : "bg-white text-gray-500 border-gray-500"
              }`}
              onClick={() => setPackageFilter("All")}
            >
              {translate("sellerPage.filters.all")}
            </button>
            <button
              className={`px-4 py-2 bg-blue-500 rounded-full font-semibold border ${
                packageFilter === "Individual"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-blue-500 border-blue-500"
              }`}
              onClick={() => setPackageFilter("Individual")}
            >
              {translate("sellerPage.filters.individual")}
            </button>
            <button
              className={`px-4 py-2 rounded-full font-semibold border ${
                packageFilter === "Corporate"
                  ? "bg-green-500 text-white"
                  : "bg-white text-green-500 border-green-500"
              }`}
              onClick={() => setPackageFilter("Corporate")}
            >
              {translate("sellerPage.filters.corporate")}
            </button>
          </div>
          <br />

          {/* Plans */}
          <div className="grid md:grid-cols-2 gap-8 w-[70%] mx-auto">
            {sellerPackages
              .filter((pkg) =>
                packageFilter === "All"
                  ? true
                  : pkg.sellerType?.name === packageFilter
              )
              .map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  className="bg-white shadow-lg rounded-lg p-6 flex flex-col"
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100,
                    damping: 15,
                  }}
                  whileHover={{
                    y: -5,
                    scale: 1.02,
                    transition: { duration: 0.2 },
                  }}
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {pkg.name}
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-sm text-white font-medium ${
                        pkg.sellerType?.name === "Corporate"
                          ? "bg-green-500"
                          : "bg-blue-500"
                      }`}
                    >
                      {pkg.sellerType?.name}
                    </span>
                  </div>

                  {/* Location */}
                  <div
                    className={`flex items-center gap-2 mb-6 text-gray-600 ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 21 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9 2C4.58172 2 1 6.00258 1 10.5C1 14.9622 3.55332 19.8124 7.53707 21.6744C8.46574 22.1085 9.53426 22.1085 10.4629 21.6744C14.4467 19.8124 17 14.9622 17 10.5C17 6.00258 13.4183 2 9 2ZM9 12C10.1046 12 11 11.1046 11 10C11 8.89543 10.1046 8 9 8C7.89543 8 7 8.89543 7 10C7 11.1046 7.89543 12 9 12Z"
                        fill="#FF4040"
                      />
                    </svg>
                    <span className="text-sm">
                      {translate("sellerPage.location")}
                    </span>
                  </div>

                  {/* Features Section */}
                  <div className="mb-6">
                    <h4
                      className={`text-lg font-semibold text-gray-900 mb-1 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("sellerPage.features.heading")}
                    </h4>
                    <p
                      className={`text-sm text-gray-500 mb-4 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {pkg.sellerType?.name === "Corporate"
                        ? translate("sellerPage.features.includedInCorporate")
                        : translate("sellerPage.features.includedInIndividual")}
                    </p>
                    <ul
                      className={`space-y-3 ${isRTL ? "space-y-reverse" : ""}`}
                    >
                      <li
                        className={`flex items-center gap-3 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                            fill="#2BC92E"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {translate("sellerPage.features.listingFee")}{" "}
                          {pkg.listingFee}
                        </span>
                      </li>
                      <li
                        className={`flex items-center gap-3 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                            fill="#2BC92E"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {translate("sellerPage.features.relistingFee")}{" "}
                          {pkg.reListingFee}
                        </span>
                      </li>
                      <li
                        className={`flex items-center gap-3 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                            fill="#2BC92E"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {translate("sellerPage.features.numberOfRuns")}{" "}
                          {pkg.numberOfRuns}
                        </span>
                      </li>
                      <li
                        className={`flex items-center gap-3 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                            fill="#2BC92E"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {translate(
                            "sellerPage.features.fineAfterFreeParking"
                          )}{" "}
                          {pkg.fineAfterFreeParkingDays}
                        </span>
                      </li>
                      <li
                        className={`flex items-center gap-3 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                            fill="#2BC92E"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {translate("sellerPage.features.freeParkingDays")}{" "}
                          {pkg.freeParkingDays}
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Pricing Table */}
                  <div className="mb-6">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-200">
                          <th
                            className={`p-3 text-sm font-semibold text-gray-700 border-gray-300 ${
                              isRTL
                                ? "text-right border-l"
                                : "text-left border-r"
                            }`}
                          >
                            {translate("sellerPage.pricingTable.startingRange")}
                          </th>
                          <th
                            className={`p-3 text-sm font-semibold text-gray-700 border-gray-300 ${
                              isRTL
                                ? "text-right border-l"
                                : "text-left border-r"
                            }`}
                          >
                            {translate("sellerPage.pricingTable.toRange")}
                          </th>
                          <th
                            className={`p-3 text-sm font-semibold text-gray-700 ${
                              isRTL ? "text-right" : "text-left"
                            }`}
                          >
                            {translate("sellerPage.pricingTable.fee")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pkg.sellerPackageRanges.map((range, idx) => (
                          <tr
                            key={idx}
                            className={
                              idx % 2 === 0 ? "bg-white" : "bg-pink-50"
                            }
                          >
                            <td
                              className={`p-3 text-sm text-gray-700 border-gray-200 ${
                                isRTL
                                  ? "text-right border-l"
                                  : "text-left border-r"
                              }`}
                            >
                              {range.startRange}
                            </td>
                            <td
                              className={`p-3 text-sm text-gray-700 border-gray-200 ${
                                isRTL
                                  ? "text-right border-l"
                                  : "text-left border-r"
                              }`}
                            >
                              {range.toRange}
                            </td>
                            <td
                              className={`p-3 text-sm text-gray-700 ${
                                isRTL ? "text-right" : "text-left"
                              }`}
                            >
                              {range.fee}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer */}

                  {/* Buy Now Button */}
                  <button className="mt-4 bg-black hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium w-full transition-colors duration-200">
                    {translate("sellerPage.buyNow")}
                  </button>
                </motion.div>
              ))}
          </div>
        </motion.div>
      </section>

      <section
        className="bg-[#F9F9F9] py-12 max-w-7xl justify-center mx-auto items-center"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <motion.div
          className="text-center mb-12"
          initial={{ y: -100, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ type: "spring", stiffness: 100, damping: 10 }}
        >
          {/* Top Title */}
          <motion.div className="text-center mb-12">
            <h2 className="text-[32px] font-bold leading-tight">
              {pageSettings?.right_image_section_2?.[0]?.title?.value ? (
                pageSettings.right_image_section_2[0].title.value
                  .split(" ")
                  .map((word, index) => (
                    <span
                      key={index}
                      className={
                        word === "Khat" ||
                        word === "Al" ||
                        word === "Jazeera" ||
                        word === "Auctions" ||
                        word === "Auction"
                          ? "text-[#F15A29]"
                          : "text-black"
                      }
                    >
                      {word}{" "}
                    </span>
                  ))
              ) : (
                <span className="text-black">Default Title</span>
              )}
            </h2>
            <p className="mt-3 text-gray-700 text-[16px] max-w-2xl mx-auto">
              {pageSettings?.right_image_section_2?.[0]?.subtitle?.value}
            </p>
          </motion.div>

          {/* Main Grey Box */}
          <div
            className={`bg-[#F3F3F3] p-8 rounded-lg shadow ${
              isRTL ? "text-right" : "text-left"
            }`}
          >
            <div
              className={`grid md:grid-cols-2 gap-8 ${
                isRTL ? "md:grid-flow-col-dense" : ""
              }`}
            >
              {/* Left Info Box */}
              <div className={`p-10 ${isRTL ? "md:order-2" : "md:order-1"}`}>
                <h3 className="text-xl font-semibold mb-5">
                  {pageSettings?.right_image_section_2?.[0]?.title_1?.value}
                </h3>
                <div
                  className="blog-content prose prose-lg text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html:
                      pageSettings?.right_image_section_2?.[0]?.description_1
                        ?.value || "",
                  }}
                />
                <h3 className="text-xl font-semibold mt-8 mb-4">
                  {pageSettings?.right_image_section_2?.[0]?.title_2?.value}
                </h3>

                <div
                  className="blog-content prose prose-lg text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html:
                      pageSettings?.right_image_section_2?.[0]?.description_2
                        ?.value || "",
                  }}
                />
              </div>

              {/* Right Form - Seller registration (clean, no background) */}
              <div
                className={`overflow-visible ${
                  isRTL ? "md:order-1" : "md:order-2"
                }`}
              >
                <div className="w-full min-h-[94vh]">
                  <MainRegistrationForm
                    tabs={["Seller"]}
                    defaultTab="Seller"
                    removeBackground={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <br />
      <section className="w-[70%] max-w-7xl justify-center mx-auto items-center p-6">
        {/* Heading */}
        <motion.div
          className="text-center mb-6"
          initial={{ x: -100, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: false }} // <-- allow re-animation
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-[32px] font-bold">
            {translate("sellerPage.faq.heading")
              .split(" ")
              .map((word, index) => (
                <span
                  key={index}
                  className={
                    word.toLowerCase() === "frequently" ||
                    word.toLowerCase() === "الأسئلة"
                      ? "text-[#F15A29]"
                      : ""
                  }
                >
                  {word}{" "}
                </span>
              ))}
          </h2>
          <p className="text-gray-600 text-[16px] mt-1">
            {translate("sellerPage.faq.subtitle")}
          </p>
        </motion.div>

        {/* FAQ List */}

        {sellerFaqs.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }} // <-- allow re-animation
            transition={{ delay: index * 0.2, duration: 0.5 }}
            className="bg-white border border-gray-300 shadow-md rounded-md mb-4 p-4 transition hover:shadow-lg"
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className={`w-full flex justify-between items-center font-semibold ${
                isRTL ? "text-right flex-row-reverse" : "text-left"
              }`}
            >
              {item.question?.value}
              <span
                className={`transition-transform duration-300 ${
                  openIndex === index ? "rotate-180" : "rotate-0"
                }`}
              >
                <svg
                  width="18"
                  height="10"
                  viewBox="0 0 18 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.8975 1.39714L9.39751 8.89714C9.29204 9.00248 9.14907 9.06165 9.00001 9.06165C8.85094 9.06165 8.70798 9.00248 8.60251 8.89714L1.10251 1.39714C1.00315 1.29051 0.949055 1.14948 0.951626 1.00375C0.954197 0.858024 1.01323 0.718985 1.11629 0.615925C1.21935 0.512865 1.35839 0.453831 1.50411 0.45126C1.64984 0.448688 1.79088 0.502781 1.89751 0.602141L9.00001 7.7037L16.1025 0.602141C16.2091 0.502781 16.3502 0.448688 16.4959 0.45126C16.6416 0.453831 16.7807 0.512865 16.8837 0.615925C16.9868 0.718985 17.0458 0.858024 17.0484 1.00375C17.051 1.14948 16.9969 1.29051 16.8975 1.39714Z"
                    fill="#131313"
                  />
                </svg>
              </span>
            </button>
            {openIndex === index && item.answer && (
              // <div className="pt-2 text-gray-600">{item.answer?.value}</div>
              <div
                className="pt-2 text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: item.answer?.value || "",
                }}
              />
            )}
          </motion.div>
        ))}
      </section>

      <br />
    </>
  );
}
